import type { QueryClient } from '@tanstack/react-query'
import { salesLeadKeys } from '@/modules/sales/hooks/useSalesLeads'
import type {
  PaginatedSalesLeads,
  SalesLead,
  SalesLeadListItem,
} from '@/modules/sales/services/sales-lead.service'
import type { NotificationItem } from '@/modules/notifications/services/notification.service'

interface LeadPayload {
  lead: SalesLead
}

interface LeadRefPayload {
  leadId: string
  businessName: string
  name: string
}

function isListKey(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === 'sales-leads' && queryKey[1] === 'list'
}

// Registered Leads tab carries a distinct DTO (RegisteredLeadListItem) — there's no
// sensible in-place patch from a SalesLead payload, so it's only ever invalidated
// (forced refetch), never patched via forEachListQuery/upsertSalesLeadInCache.
function isRegisteredListKey(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === 'sales-leads' && queryKey[1] === 'registered-list'
}

// List rows carry a lean registeredLead summary (rlNo/status/itemCount/firstProductName),
// while write/detail responses carry the full registered record (items array, closing
// fields, …) — project the full shape down before it goes into a list cache.
function toListItem(lead: SalesLead): SalesLeadListItem {
  const { registeredLead, productLines, ...rest } = lead
  const lines = productLines ?? []
  return {
    ...rest,
    firstProductDescription: lines[0]?.description ?? null,
    firstCategoryName: lines[0]?.categoryNameSnapshot ?? null,
    productLineCount: lines.length,
    totalQuantity:
      lead.totalQuantity ??
      (lines.length
        ? lines.reduce<number | null>((sum, line) => {
            if (line.quantity == null) return sum
            return (sum ?? 0) + line.quantity
          }, null)
        : null),
    registeredLead: registeredLead
      ? {
          rlNo: registeredLead.rlNo,
          registeredStatus: registeredLead.registeredStatus,
          itemCount: registeredLead.items.length,
          firstProductName: registeredLead.items[0]?.productNameSnapshot ?? null,
        }
      : null,
  }
}

/** Iterates every cached sales-leads list query, letting each be patched with its own params. */
function forEachListQuery(
  qc: QueryClient,
  fn: (data: PaginatedSalesLeads, queryKey: readonly unknown[]) => PaginatedSalesLeads,
) {
  const matches = qc.getQueriesData<PaginatedSalesLeads>({
    predicate: (query) => isListKey(query.queryKey),
  })
  for (const [queryKey, data] of matches) {
    if (!data) continue
    qc.setQueryData(queryKey, fn(data, queryKey))
  }
}

function invalidateLists(qc: QueryClient) {
  void qc.invalidateQueries({
    predicate: (query) => isListKey(query.queryKey) || isRegisteredListKey(query.queryKey),
  })
}

/**
 * Insert-or-replace a lead in every cached list + the detail cache. Used both for live
 * WebSocket pushes (sales_lead.assigned) and for the acting user's own mutation response —
 * the actor never gets a WebSocket event for their own create/update/remark, so create/update/
 * remark mutations call this directly with the POST/PATCH response.
 */
export function upsertSalesLeadInCache(qc: QueryClient, lead: SalesLead) {
  const listItem = toListItem(lead)
  forEachListQuery(qc, (data, queryKey) => {
    if (data.data.some((row) => row.id === lead.id)) {
      return { ...data, data: data.data.map((row) => (row.id === lead.id ? listItem : row)) }
    }
    const params = queryKey[2] as
      | { page?: number; assignedUserId?: string; assignedUserIds?: string[] }
      | undefined
    const isFirstPage = !params?.page || params.page === 1
    const matchesAssignee =
      (!params?.assignedUserId || params.assignedUserId === lead.assignedUserId) &&
      (!params?.assignedUserIds?.length || params.assignedUserIds.includes(lead.assignedUserId))
    if (!isFirstPage || !matchesAssignee) return data

    const next = [listItem, ...data.data]
    if (next.length > data.pageSize) next.pop()
    return { ...data, data: next, total: data.total + 1 }
  })
  qc.setQueryData(salesLeadKeys.detail(lead.id), lead)
}

function replaceLead(qc: QueryClient, lead: SalesLead) {
  const listItem = toListItem(lead)
  forEachListQuery(qc, (data) => {
    if (!data.data.some((row) => row.id === lead.id)) return data
    return { ...data, data: data.data.map((row) => (row.id === lead.id ? listItem : row)) }
  })
  if (qc.getQueryData(salesLeadKeys.detail(lead.id)) !== undefined) {
    qc.setQueryData(salesLeadKeys.detail(lead.id), lead)
  }
}

function removeLead(qc: QueryClient, leadId: string) {
  forEachListQuery(qc, (data) => {
    if (!data.data.some((row) => row.id === leadId)) return data
    return {
      ...data,
      data: data.data.filter((row) => row.id !== leadId),
      total: Math.max(0, data.total - 1),
    }
  })
  qc.removeQueries({ queryKey: salesLeadKeys.detail(leadId) })
}

/** Applies a live sales_lead.* notification to the react-query cache — no reload, no polling. */
export function applySalesLeadNotification(qc: QueryClient, notification: NotificationItem) {
  const payload = notification.payload as Partial<LeadPayload & LeadRefPayload> | null

  switch (notification.type) {
    case 'sales_lead.assigned':
      if (payload?.lead) upsertSalesLeadInCache(qc, payload.lead)
      break
    case 'sales_lead.updated':
    case 'sales_lead.remarked':
    case 'sales_lead.contact_logged':
    case 'sales_lead.registered':
    case 'sales_lead.closed':
      if (payload?.lead) replaceLead(qc, payload.lead)
      break
    case 'sales_lead.site_enquiry':
      // Website form: new lead or a repeat enquiry — lists + detail refresh below.
      break
    case 'sales_lead.unassigned':
    case 'sales_lead.deleted':
      if (payload?.leadId) removeLead(qc, payload.leadId)
      break
    default:
      return
  }

  invalidateLists(qc)
  if (notification.entityId) {
    void qc.invalidateQueries({ queryKey: salesLeadKeys.detail(notification.entityId) })
  }
}
